import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE = "https://www.infrablaze.com";
const DEFAULT_IMG = `${SITE}/images/hero-image-dark.png`;
const DEFAULT_TITLE = "InfraBlaze — Control Plane for Agent Driven Infrastructure";
const DEFAULT_DESC =
  "InfraBlaze is the control plane for agent-driven infrastructure. Design, cost, review, enforce and verify cloud infra across AWS, Azure, and GCP — one workbench for IaC, policy, and drift.";

const JSON_LD_ID = "page-jsonld";

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[(\w+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(data) {
  // Remove any per-route JSON-LD from a previous render; keep the site-wide
  // graph in index.html intact.
  const existing = document.getElementById(JSON_LD_ID);
  if (existing) existing.remove();
  if (!data) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = JSON_LD_ID;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Per-route SEO metadata. Writes <title>, description, canonical, OG/Twitter,
 * and optional JSON-LD structured data. Every write is idempotent so React 18
 * StrictMode's double-invocation is harmless.
 *
 * Accepts `jsonLd` — either a single schema.org object or array — which is
 * emitted as a single `<script id="page-jsonld">` tag and replaced on route
 * change.
 *
 * Crawlers that execute JS (Googlebot, Bingbot) see per-route metadata.
 * Crawlers that don't get the defaults from index.html — prerendering (react-
 * snap) bakes per-route HTML at build time so even non-JS AI bots see the
 * right title/description/JSON-LD.
 */
export default function PageMeta({
  title,
  description = DEFAULT_DESC,
  image = DEFAULT_IMG,
  keywords,
  type = "website",
  jsonLd,
  canonicalPath,
}) {
  const location = useLocation();
  // `canonicalPath` pins the canonical URL for pages reachable at more than
  // one path (e.g. /privacy and /privacy-policy render the same component).
  // Without it, each alias would self-canonicalise and read as duplicate
  // content.
  const path = canonicalPath || (location.pathname === "/" ? "/" : location.pathname);
  const canonical = `${SITE}${path}`;
  const fullTitle = title
    ? title.endsWith("InfraBlaze") || title.includes("InfraBlaze")
      ? title
      : `${title} · InfraBlaze`
    : DEFAULT_TITLE;

  useEffect(() => {
    document.title = fullTitle;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="keywords"]', "content", keywords);
    setLink("canonical", canonical);

    setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[property="og:site_name"]', "content", "InfraBlaze");
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[property="og:image"]', "content", image);

    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", image);

    setJsonLd(jsonLd);
  }, [fullTitle, description, keywords, canonical, image, type, jsonLd]);

  return null;
}
